import { shell } from '../shell/shellClasses';
import type { ActionPriority, BusinessArea, DecisionFiltersState } from './types';
import { PRIORITY_LABEL, PRIORITY_ORDER } from './types';

const AREAS: BusinessArea[] = [
  'connect',
  'orders',
  'inventory',
  'sync',
  'pricing',
  'finance',
  'fulfillment',
  'catalog',
  'other',
];

export function DecisionFilters({
  filters,
  onChange,
  workspaces,
  sellers,
  marketplaces,
}: {
  filters: DecisionFiltersState;
  onChange: (next: DecisionFiltersState) => void;
  workspaces: string[];
  sellers: { value: string; label: string }[];
  marketplaces: string[];
}) {
  const set = <K extends keyof DecisionFiltersState>(
    key: K,
    value: DecisionFiltersState[K],
  ) => onChange({ ...filters, [key]: value });

  const control = `${shell.control} ${shell.focusRing} text-xs`;

  return (
    <fieldset
      className={`rounded-[var(--shell-radius-lg)] border p-3 sm:p-4 ${shell.border} ${shell.surface}`}
    >
      <legend className={`px-1 text-sm font-semibold text-[var(--shell-fg)]`}>
        Decision filters
      </legend>
      <div className="mt-2 grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
        <label className="flex flex-col gap-1 text-xs">
          <span className={shell.muted}>Priority</span>
          <select
            className={control}
            value={filters.priority}
            onChange={(e) =>
              set('priority', e.target.value as ActionPriority | 'all')
            }
          >
            <option value="all">All priorities</option>
            {PRIORITY_ORDER.map((p) => (
              <option key={p} value={p}>
                {PRIORITY_LABEL[p]}
              </option>
            ))}
          </select>
        </label>

        <label className="flex flex-col gap-1 text-xs">
          <span className={shell.muted}>Workspace</span>
          <select
            className={control}
            value={filters.workspace}
            onChange={(e) => set('workspace', e.target.value)}
          >
            <option value="all">All workspaces</option>
            {workspaces.map((w) => (
              <option key={w} value={w}>
                {w}
              </option>
            ))}
          </select>
        </label>

        <label className="flex flex-col gap-1 text-xs">
          <span className={shell.muted}>Business area</span>
          <select
            className={control}
            value={filters.businessArea}
            onChange={(e) =>
              set('businessArea', e.target.value as BusinessArea | 'all')
            }
          >
            <option value="all">All areas</option>
            {AREAS.map((a) => (
              <option key={a} value={a}>
                {a}
              </option>
            ))}
          </select>
        </label>

        <label className="flex flex-col gap-1 text-xs">
          <span className={shell.muted}>Seller</span>
          <select
            className={control}
            value={filters.seller}
            onChange={(e) => set('seller', e.target.value)}
          >
            <option value="all">All sellers</option>
            {sellers.map((s) => (
              <option key={s.value} value={s.value}>
                {s.label}
              </option>
            ))}
          </select>
        </label>

        <label className="flex flex-col gap-1 text-xs">
          <span className={shell.muted}>Marketplace</span>
          <select
            className={control}
            value={filters.marketplace}
            onChange={(e) => set('marketplace', e.target.value)}
          >
            <option value="all">All marketplaces</option>
            {marketplaces.map((m) => (
              <option key={m} value={m}>
                {m}
              </option>
            ))}
          </select>
        </label>

        <label className="flex flex-col gap-1 text-xs">
          <span className={shell.muted}>From date</span>
          <input
            type="date"
            className={control}
            value={filters.dateFrom}
            onChange={(e) => set('dateFrom', e.target.value)}
          />
        </label>

        <label className="flex flex-col gap-1 text-xs">
          <span className={shell.muted}>To date</span>
          <input
            type="date"
            className={control}
            value={filters.dateTo}
            onChange={(e) => set('dateTo', e.target.value)}
          />
        </label>
      </div>
    </fieldset>
  );
}
